import type { MaybeRef, Ref } from 'vue';
import { ref, toValue, watch, watchEffect } from 'vue';
import { refDebounced, useStorage } from '@vueuse/core';
import { STORAGE_KEY_ITEMS_PER_PAGE } from '~/utilities/constants';
import pyscriptApi from '~/utilities/pyscript-api';
import type {
  ListProjectsOptions,
  ListProjectsResponse,
  Project,
} from '~/utilities/pyscript-api-models';
import { useSortPreference } from '~/composables/sort-preference';

const { savedSort } = useSortPreference();

export type ProjectPagination = Omit<ListProjectsResponse, 'results'>;

export interface UsePaginatedProjectListOptions {
  user?: MaybeRef<ListProjectsOptions['user']>;
  /** Only used if fetching projects for a collection. */
  collectionId?: string;
  q?: Ref<ListProjectsOptions['q']>;
  scope?: MaybeRef<ListProjectsOptions['scope']>;
}

export interface UsePaginatedProjectListReturn {
  projectList: Ref<Project[] | null>;
  pagination: Ref<ProjectPagination | null>;
  setPage: (value: number) => void;
  refetch: () => Promise<void>;
  hasNoProjects: Ref<boolean>;
}

export type ItemsPerPageOptions = 10 | 20 | 50 | 100;

/** Local storage saved value for number of items to show per page. */
export const savedItemsPerPage = useStorage<ItemsPerPageOptions>(STORAGE_KEY_ITEMS_PER_PAGE, 100);

export function usePaginatedProjectList(
  options: UsePaginatedProjectListOptions,
): UsePaginatedProjectListReturn {
  const controller = ref<AbortController>();
  const query = refDebounced(ref(options.q), 300);

  // State
  const page = ref(1);
  const projectList = ref<Project[] | null>(null);
  const pagination = ref<ProjectPagination | null>(null);
  const hasNoProjects = ref<boolean>(false);

  // Reset the state when the `options.user` changes.
  watch(
    () => options.user,
    () => {
      page.value = 1;
      projectList.value = null;
      pagination.value = null;
    },
    { deep: true },
  );

  // Reset the page number when the page size, query, or sort changes.
  watch([savedItemsPerPage, query, savedSort], () => (page.value = 1));

  watchEffect(async () => {
    // Cancel the previous request.
    if (controller.value) {
      controller.value.abort();
    }

    controller.value = new AbortController();
    const signal = controller.value.signal;

    try {
      await listProjects(signal);
    } catch (error) {}
  });

  async function listProjects(signal?: AbortSignal) {
    let resp: ListProjectsResponse;

    if (options.collectionId) {
      resp = await pyscriptApi.listProjectsInCollection(
        options.collectionId,
        {
          scope: toValue(options.scope),
          user: toValue(options.user),
          q: toValue(query),
          pageSize: savedItemsPerPage.value,
          page: page.value,
          sortBy: savedSort.value.sortBy,
          sortOrder: savedSort.value.sortOrder,
        },
        signal,
      );
    } else {
      resp = await pyscriptApi.listProjects(
        {
          scope: toValue(options.scope),
          user: toValue(options.user),
          q: toValue(query),
          pageSize: savedItemsPerPage.value,
          page: page.value,
          sortBy: savedSort.value.sortBy,
          sortOrder: savedSort.value.sortOrder,
        },
        signal,
      );
    }

    if (resp) {
      const { results, ...paginationData } = resp;

      projectList.value = results;
      pagination.value = paginationData;
      hasNoProjects.value = !query.value && results.length === 0;
    }
  }

  function setPage(value: number) {
    page.value = value;
  }

  async function refetch() {
    await listProjects();
  }

  return {
    projectList,
    pagination,
    setPage,
    refetch,
    hasNoProjects,
  };
}
